import React, {
  useState,
  useCallback,
  useEffect,
  CSSProperties,
  ReactNode,
} from "react";
import {
  InfiniteCarouselProps,
  TransitionClass,
  SlidePosition,
  SlideStyle,
} from "../../utility/types";

import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const InfiniteCarousel: React.FC<InfiniteCarouselProps> = ({
  slides = ["0", "1", "2"],
  className = "",
  containerStyle = {},
  slideStyle = {},
  buttonStyle = {},
  animationDuration = 300,
  onSlideChange,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(slides.length);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [transitionClass, setTransitionClass] = useState<TransitionClass>("");

  // Create triple array for infinite scroll
  const tripleSlides: ReactNode[] = [...slides, ...slides, ...slides];

  // Notify parent component of slide changes
  useEffect(() => {
    if (onSlideChange) {
      const actualIndex = currentIndex % slides.length;
      onSlideChange(actualIndex);
    }
  }, [currentIndex, slides.length, onSlideChange]);

  // Generate inline styles for positioning
  const getTransformStyle = useCallback(
    (index: number): CSSProperties => {
      const offset = -1 * (75 * index) + 12.5;
      return {
        marginLeft: `calc(${offset}vw - ${index * 2}rem)`,
        transition:
          transitionClass === "no-animate" ? "none" : `margin-left 0.5s ease`,
      };
    },
    [transitionClass]
  );

  // Get slide class based on position
  const getSlidePosition = useCallback(
    (slideIndex: number, currentIndex: number): SlidePosition => {
      if (slideIndex < currentIndex) return "small";
      if (slideIndex === currentIndex) return "medium";
      return "large";
    },
    []
  );

  const goToPrevious = useCallback((): void => {
    if (isAnimating) return;

    setIsAnimating(true);
    setTransitionClass("prev");

    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);

    setTimeout(() => {
      if (newIndex === 0) {
        setTransitionClass("no-animate");
        setCurrentIndex(slides.length * 2);
        setTimeout(() => setTransitionClass(""), 50);
      } else {
        setTransitionClass("");
      }
      setIsAnimating(false);
    }, animationDuration);
  }, [isAnimating, currentIndex, slides.length, animationDuration]);

  const goToNext = useCallback((): void => {
    if (isAnimating) return;

    setIsAnimating(true);
    setTransitionClass("next");

    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);

    setTimeout(() => {
      if (newIndex === slides.length * 2) {
        setTransitionClass("no-animate");
        setCurrentIndex(slides.length);
        setTimeout(() => setTransitionClass(""), 50);
      } else {
        setTransitionClass("");
      }
      setIsAnimating(false);
    }, animationDuration);
  }, [isAnimating, currentIndex, slides.length, animationDuration]);

  // Style objects with proper typing
  const defaultContainerStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "400px",
    overflow: "hidden",
    ...containerStyle,
  };

  const sectionStyle: CSSProperties = {
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "100%",
    height: "calc(100% + 2rem)",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-end",
    gap: "2rem",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    overflow: "hidden",
    transition:
      transitionClass === "no-animate"
        ? "none"
        : `transform ${animationDuration}ms ease`,
    transform:
      transitionClass === "prev"
        ? "translateX(0.5rem)"
        : transitionClass === "next"
        ? "translateX(-0.5rem)"
        : "translateX(0)",
    pointerEvents: "none",
    padding: "1rem 0",
  };

  const slidesContainerStyle: CSSProperties = {
    display: "flex",
    gap: "2rem",
    alignItems: "flex-end",
    ...getTransformStyle(currentIndex),
  };

  const getSlideStyle = useCallback(
    (slideIndex: number, currentIndex: number): CSSProperties => {
      const position = getSlidePosition(slideIndex, currentIndex);
      const isSmall = position === "small";
      const isMedium = position === "medium";
      const isLarge = position === "large";

      const baseStyle: CSSProperties = {
        flexShrink: 0,
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        width: isMedium ? "50vw" : "75vw",
        fontSize: "2rem",
        color: "white",
        transition: transitionClass === "no-animate" ? "none" : `all 0.5s ease`,
        borderRadius: "16px",
        padding: "0",
        height: isSmall ? "224px" : isMedium ? "280px" : "336px",
        opacity: isSmall || isLarge ? 0.3 : 1,
        ...slideStyle,
      };

      return baseStyle;
    },
    [getSlidePosition, transitionClass, slideStyle]
  );

  const defaultButtonStyle: CSSProperties = {
    position: "absolute",
    top: "calc(50% - 2rem)",
    width: "4rem",
    height: "4rem",
    border: "none",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 4px 12px 0 rgba(40, 40, 40, 0.14)",
    fontSize: "1.5rem",
    cursor: "pointer",
    pointerEvents: "auto",
    transition: "background-color 0.2s ease",
    zIndex: 10,
    ...buttonStyle,
  };

  const handleButtonMouseEnter = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    if (!isAnimating) {
      e.currentTarget.style.backgroundColor = "rgba(255,255,255, 0.93)";
    }
  };

  const handleButtonMouseLeave = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    e.currentTarget.style.backgroundColor = "#FFF";
  };

  const images: Record<number, string> = {
    0: "/images/templates/trellis.png",
    1: "/images/templates/deepseek.png",
    2: "/images/templates/dalle.png",
  };

  const imgUrl = (currentIdx: number) => {
    let newIdx = currentIdx % 3;
    return images[newIdx];
  };

  return (
    <div className={className} style={defaultContainerStyle}>
      <div style={sectionStyle}>
        <div style={slidesContainerStyle}>
          {tripleSlides.map((slide: ReactNode, index: number) => (
            <div
              key={`slide-${index}`}
              style={{
                ...getSlideStyle(index, currentIndex),
                background: `linear-gradient(to top, rgba(16, 16, 16, 0.7), rgba(0, 0, 0, 0)), url(${imgUrl(
                  index
                )}) center / cover no-repeat`,
              }}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      <button
        style={{ ...defaultButtonStyle, left: "1rem" }}
        onClick={goToPrevious}
        disabled={isAnimating}
        onMouseEnter={handleButtonMouseEnter}
        onMouseLeave={handleButtonMouseLeave}
        aria-label="Previous slide"
      >
        <LeftOutlined />
      </button>
      <button
        style={{ ...defaultButtonStyle, right: "1rem" }}
        onClick={goToNext}
        disabled={isAnimating}
        onMouseEnter={handleButtonMouseEnter}
        onMouseLeave={handleButtonMouseLeave}
        aria-label="Next slide"
      >
        <RightOutlined />
      </button>
    </div>
  );
};

export default InfiniteCarousel;
